import Vue from 'vue';
import { createStore } from 'vuex';
import Bowser from 'bowser';
import { useRoute } from 'vue-router';
import { cloneDeep } from 'lodash/fp';
import requests from './requests.js';

// Module imports
import project from './project/project.js';
import application from './application/application.js';
import user from './user/user.js';
import settings from './settings/settings.js';
import updater from './project/updater.js';
import playMode from './play-mode/play-mode.js';
import iconMetadata from './icon-metadata.js';
import workspace from './workspace/workspace.js';
import permissions from './permissions.js';
import features from './features.js';
import featureFlags from './featureFlags.js';

const store = createStore({
  strict: process.env.NODE_ENV !== 'production',
  modules: {
    project,
    application,
    user,
    settings,
    playMode,
    workspace,
    permissions,
    features,
    featureFlags,
  },
  state: {
    serverTimeDiff: 0,
    bowser: null,
  },
  getters: {
    getServerTimeDiff(state) {
      return state.serverTimeDiff;
    },

    getBowser(state) {
      return state.bowser;
    },
  },
  actions: {
    /**
     * Set the settings to the given value if not null, else reset the state
     */
    setSettings(context, { value }) {
      context.commit('setSettings', {
        value: value || {
          assetsUrl: null,
          roles: [],
          plans: null,
          countries: null,
          euCountries: null,
        },
      });
    },

    /**
     * Load all projects information for this user
     */
    loadProjects(context) {
      return requests.projects.loadProjects().then(response => {
        context.dispatch('user/setProjects', {
          value: response.data,
        });
        return Promise.resolve(response);
      });
    },

    /**
     * Load all initial information (projects, user and settings)
     */
    async initialize(context) {
      context.commit('application/appStates/setIsInitializing', {
        value: true,
      });

      // Store the subscription plans
      const subscriptionPlans = await requests.subscriptionPlans();
      context.commit('application/setSubscriptionPlans', {
        plans: subscriptionPlans,
      });

      const bowser = {
        parser: Bowser.getParser(window.navigator.userAgent),
      };
      bowser.safari = bowser.parser.is('safari');
      bowser.firefox = bowser.parser.is('firefox');
      bowser.chrome = bowser.parser.is('chrome');
      bowser.mac = bowser.parser.is('macOS');
      bowser.windows = bowser.parser.is('windows');
      bowser.desktop = bowser.parser.is('desktop');
      bowser.is = bowser.parser.is;

      context.commit('setBowser', { value: bowser });

      // Load the initial data and set the states
      const initializeRes = await requests.initialize();
      const {
        settings: appSettings,
        projects,
        account,
        metadata,
      } = initializeRes.data;

      context.dispatch('setSettings', { value: appSettings });
      await context.dispatch('user/setAccount', { value: account });

      context.dispatch('user/setProjects', { value: JSON.parse(projects) });

      await iconMetadata.initialize(
        JSON.parse(metadata.tagTree),
        JSON.parse(metadata.metadata),
        JSON.parse(metadata.artists),
        JSON.parse(metadata.appIcons)
      );

      return context.commit('application/appStates/setIsInitializing', {
        value: false,
      });
    },

    async loadWorkspace(
      context,
      { workspaceHash = null, projectHash = null, isLoggedIn = false } = {}
    ) {
      const workspaceRes = await requests.workspaces.init({
        workspaceHash,
        projectHash,
      });

      const [connections, permissionsRes] = await Promise.all([
        isLoggedIn
          ? requests.me.workspaceConnections(workspaceRes.workspace.id)
          : Promise.resolve(null),
        workspaceRes?.workspace?.id
          ? requests.permissions.current({
              workspaceId: workspaceRes.workspace.id,
              projectHash,
            })
          : Promise.resolve(null),
      ]);

      if (connections) {
        context.dispatch('user/setMemberships', {
          value: connections.memberships,
        });
        context.dispatch('user/setCurrentMembership', {
          value: connections.currentMembership,
        });
        context.dispatch('user/setExternalMemberships', {
          value: connections.externalMemberships,
        });
      }

      context.dispatch('featureFlags/setFeatureFlags', {
        featureFlags: workspaceRes.featureFlags,
      });

      context.dispatch('features/setFeatures', {
        features: workspaceRes.features,
      });
      context.dispatch('permissions/setPermissions', permissionsRes);

      return context.dispatch('workspace/setWorkspace', {
        value: workspaceRes.workspace,
      });
    },

    /**
     * Load a specific project
     * @param {string} hash   The project hash
     */
    async loadProject(context, { hash }) {
      context.commit('application/appStates/setIsProjectLoading', {
        value: true,
      });

      const currentRoute = useRoute();

      return requests.projects
        .loadProject({
          hash,
          routeName: currentRoute?.name,
          entryPoint: currentRoute?.query.entrypoint,
        })
        .then(response => {
          const {
            data,
            config,
            name,
            urlHash,
            publicLink,
            version,
            externalUsers,
            ownerId,
            cover,
            coverUrl,
            workspaceId,
          } = response.data;
          console.log('Load project response:', response.data);
          context.commit('project/setData', { value: JSON.parse(data) });
          context.commit('project/setConfig', { value: JSON.parse(config) });
          context.commit('project/setName', { value: name });
          context.commit('project/setHash', { value: urlHash });
          context.commit('project/setCover', { value: cover });
          context.commit('project/setCoverUrl', { value: coverUrl });
          context.commit('project/setPublicLink', { value: publicLink });
          context.commit('project/setExternalUsers', { value: externalUsers });
          context.commit('project/setOwnerId', { value: ownerId });
          context.commit('project/setWorkspaceId', { value: workspaceId });

          // This is now the user's open project (for guest also)
          context.commit('user/projects/setOpen', { hash });

          // Initialize updater
          const state = {
            data: context.getters['project/getData'],
            config: context.getters['project/getConfig'],
          };
          const v = context.getters['project/getVersion'];
          updater.initialize(hash, state, version, this);
          updater.validateStartingState();

          return Promise.resolve(response);
        })
        .finally(() => {
          context.commit('application/appStates/setIsProjectLoading', {
            value: false,
          });
        });
    },

    /**
     * Upload an project cover either from a url (or as base64)
     * @param {string} data Either the image data as base64 encoded string or a url (could be data url or normal url)
     * @param {string} hash The project hash
     */
    uploadCover(context, { data, hash }) {
      return requests.assets.uploadCover({ data, hash }).then(response => {
        const { cover: value } = response.data;
        console.log('Uploaded cover, the value is:', value);
        return Promise.resolve(response);
      });
    },

    /**
     * Remove a project cover
     * @param {string} hash The project hash
     * @param {string} file The cover filename
     * @param {string} type The cover type
     */
    removeCover(context, { hash, file, type }) {
      return requests.assets
        .removeCover({ hash, file, type })
        .then(response => {
          return Promise.resolve(response);
        });
    },

    /**
     * Load the server time
     */
    async getServerTime(context) {
      return requests.getServerTime().then(response => {
        const serverTime = new Date(response.data);
        // console.log('The server date time is:', serverTime);
        const clientTime = new Date();
        // console.log('The client date time is:', clientTime);
        const diff = Math.round(
          (clientTime.getTime() - serverTime.getTime()) / 1000
        );
        // console.log('Server time diff is:', diff);
        context.commit('setServerTimeDiff', { value: diff });
        return Promise.resolve(response);
      });
    },
  },

  // Mutations of states
  mutations: {
    /**
     * Set the state projects to the given one
     * @param {Object} value  The projects
     */
    setSettings(state, { value }) {
      state.settings = cloneDeep(value);
    },

    /**
     * Set the server time difference
     */
    setServerTimeDiff(state, { value }) {
      state.serverTimeDiff = value;
    },

    setBowser(state, { value }) {
      state.bowser = value;
    },
  },
});

const unsubscribe = store.subscribeAction((action, state) => {
  const startingStr = 'project/data/';
  if (action.type.startsWith(startingStr)) {
    updater.addAction(action.type.substring(startingStr.length));
  }
});

export default store;
